import React, { useState, useEffect } from 'react'
import { Modal, Radio, Button, Card, Typography } from 'antd'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { updateCustomIngredientAllergens } from 'src/features/RecipeBuilderSlice'

const { Title, Text } = Typography

const commonAllergens = [
  'Celery',
  'Gluten',
  'Crustaceans',
  'Eggs',
  'Fish',
  'Lupin',
  'Milk',
  'Molluscs',
  'Mustard',
  'Nuts',
  'Peanuts',
  'Sesame seeds',
  'Soya',
  'Sulphites',
]

const EditCustomIngredientModal = ({
  showEditCustomIngredient,
  setShowEditCustomIngredient,
  customIngredient,
  formState,
  setFormState,
  index,
  onAllergenUpdate,
}) => {
  const dispatch = useDispatch()
  const venueId = useSelector(state => state.venue?.venue?._id)
  const [selectedAllergens, setSelectedAllergens] = useState([])
  const [selectedTraces, setSelectedTraces] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (customIngredient && Object.keys(customIngredient).length > 0) {
      // Initialize allergens and traces from the custom ingredient
      // Check both direct properties and nested product properties
      const allergens =
        customIngredient.allergens || customIngredient.product?.allergens || []
      const allergenTraces =
        customIngredient.allergenTraces ||
        customIngredient.product?.allergenTraces ||
        []

      console.log('EditCustomIngredientModal: Initializing allergens:', {
        customIngredient,
        allergens,
        allergenTraces,
        directAllergens: customIngredient.allergens,
        productAllergens: customIngredient.product?.allergens,
      })

      setSelectedAllergens(allergens)
      setSelectedTraces(allergenTraces)
    }
  }, [customIngredient])

  const handleAllergenChange = (allergen, value) => {
    if (value === 'yes') {
      setSelectedAllergens(prev => [
        ...prev.filter(a => a !== allergen),
        allergen,
      ])
      setSelectedTraces(prev => prev.filter(a => a !== allergen))
    } else if (value === 'traces') {
      setSelectedAllergens(prev => prev.filter(a => a !== allergen))
      setSelectedTraces(prev => [...prev.filter(a => a !== allergen), allergen])
    } else {
      setSelectedAllergens(prev => prev.filter(a => a !== allergen))
      setSelectedTraces(prev => prev.filter(a => a !== allergen))
    }
  }

  const handleSave = async () => {
    if (index !== null && formState.customProducts[index]) {
      setSaving(true)

      try {
        // Update form state first
        const updatedProducts = [...formState.customProducts]
        const updatedProduct = {
          ...updatedProducts[index],
          allergens: selectedAllergens,
          allergenTraces: selectedTraces,
        }

        // If this is an existing custom ingredient, also update the product data
        if (updatedProduct.product) {
          updatedProduct.product = {
            ...updatedProduct.product,
            allergens: selectedAllergens,
            allergenTraces: selectedTraces,
          }
        }

        updatedProducts[index] = updatedProduct

        console.log('EditCustomIngredientModal: Updating form state with:', {
          index,
          originalProduct: formState.customProducts[index],
          updatedProduct,
          allProducts: updatedProducts,
          isNewlyCreated: !customIngredient.product?._id,
          hasRecipeId: !!formState._id,
        })

        setFormState(prev => {
          console.log(
            'EditCustomIngredientModal: Setting form state from:',
            prev.customProducts,
            'to:',
            updatedProducts,
          )
          return {
            ...prev,
            customProducts: updatedProducts,
          }
        })

        // Only attempt database update for existing custom ingredients that have been saved
        // For newly created custom ingredients, we only update the form state
        const isExistingCustomIngredient =
          customIngredient.product?._id && formState._id

        if (isExistingCustomIngredient) {
          console.log(
            'EditCustomIngredientModal: Saving custom ingredient allergens to database:',
            {
              recipeId: formState._id,
              ingredientId:
                customIngredient.product?._id || customIngredient._id,
              allergens: selectedAllergens,
              allergenTraces: selectedTraces,
            },
          )

          try {
            const result = await dispatch(
              updateCustomIngredientAllergens(
                formState._id,
                customIngredient.product?._id || customIngredient._id,
                {
                  allergens: selectedAllergens,
                  allergenTraces: selectedTraces,
                  venueId: venueId,
                },
              ),
            )

            console.log(
              'EditCustomIngredientModal: Database update result:',
              result,
            )
          } catch (dbError) {
            console.error(
              'EditCustomIngredientModal: Database update failed:',
              dbError,
            )
            // Don't close the modal if database update fails
            setSaving(false)
            return
          }
        } else {
          console.log(
            'EditCustomIngredientModal: Skipping database update for newly created custom ingredient (no product ID yet)',
          )
        }

        console.log(
          'EditCustomIngredientModal: Closing modal and triggering refresh',
        )
        setShowEditCustomIngredient(false)

        // Only trigger data refresh for allergen grids if we actually saved to database
        if (isExistingCustomIngredient && onAllergenUpdate) {
          console.log(
            'EditCustomIngredientModal: Calling onAllergenUpdate after database save',
          )
          onAllergenUpdate()
        } else {
          console.log(
            'EditCustomIngredientModal: Skipping onAllergenUpdate for newly created custom ingredient or no database save occurred',
          )
        }
      } catch (error) {
        console.error(
          'EditCustomIngredientModal: Error saving custom ingredient allergens:',
          error,
        )
        // Don't close the modal if there's an error
      } finally {
        setSaving(false)
      }
    }
  }

  const handleCancel = () => {
    setShowEditCustomIngredient(false)
  }

  return (
    <Modal
      title={`Edit Allergens - ${
        customIngredient?.product?.title ||
        customIngredient?.title ||
        'Custom Product'
      }`}
      visible={showEditCustomIngredient}
      onCancel={handleCancel}
      width={800}
      footer={[
        <Button key='cancel' onClick={handleCancel}>
          Cancel
        </Button>,
        <Button key='save' type='primary' onClick={handleSave} loading={saving}>
          Save Changes
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Left side - Allergen selection */}
        <div style={{ flex: 1 }}>
          <Title level={4}>Allergen Information</Title>
          <Text
            type='secondary'
            style={{ marginBottom: '16px', display: 'block' }}
          >
            Select allergens present in this custom product. Choose
            &quot;Yes&quot; for definite presence, &quot;May Contain&quot; for
            possible traces, or &quot;No&quot; for no presence.
          </Text>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '12px',
            }}
          >
            {commonAllergens.map(allergen => {
              const hasAllergen = selectedAllergens.includes(allergen)
              const hasTraces = selectedTraces.includes(allergen)
              const currentValue = hasAllergen
                ? 'yes'
                : hasTraces
                ? 'traces'
                : 'no'

              return (
                <Card key={allergen} size='small' style={{ padding: '8px' }}>
                  <div style={{ marginBottom: '8px', fontWeight: '500' }}>
                    {allergen}
                  </div>
                  <Radio.Group
                    value={currentValue}
                    onChange={e =>
                      handleAllergenChange(allergen, e.target.value)
                    }
                    size='small'
                  >
                    <Radio value='no'>No</Radio>
                    <Radio value='traces'>May Contain</Radio>
                    <Radio value='yes'>Yes</Radio>
                  </Radio.Group>
                </Card>
              )
            })}
          </div>
        </div>

        {/* Right side - Information box */}
        <div style={{ flex: 0.4 }}>
          <InfoCard>
            <Title
              level={5}
              style={{
                color: '#b4d820',
                marginBottom: '12px',
                fontSize: '14px',
              }}
            >
              Allergen Information
            </Title>
            <Text style={{ fontSize: '12px', lineHeight: '1.4' }}>
              <strong>Yes:</strong> The allergen is definitely present in this
              product.
              <br />
              <br />
              <strong>May Contain:</strong> The allergen may contain traces due
              to cross-contamination or shared equipment.
              <br />
              <br />
              <strong>No:</strong> The allergen is not present in this product.
            </Text>
          </InfoCard>
        </div>
      </div>
    </Modal>
  )
}

const InfoCard = styled(Card)`
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;

  .ant-card-body {
    padding: 16px;
  }
`

export default EditCustomIngredientModal
